'use client';

import { ReactElement, useCallback } from 'react';
import PrismAPI from '@modules/prism-client/types/prism-types';
import { logger } from '@tuic/logger';

type WithAlertDismissProps = {
  id?: string;
  dismissAction?: PrismAPI.ActionDefinition;
  onDismiss?: (alertId: string) => void;
  children: (props: { onClose?: () => Promise<void> }) => ReactElement;
};

export const WithAlertDismiss = ({
  id,
  dismissAction,
  onDismiss,
  children,
}: WithAlertDismissProps) => {
  const handleClose = useCallback(async () => {
    if (!dismissAction || !id) return;

    // Immediately notify a parent component to update the local state for better UX
    if (onDismiss) {
      onDismiss(id);
    }

    // Call server dismisses action in the background
    try {
      const response = await fetch(dismissAction.endpoint, {
        method: dismissAction.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: dismissAction.token,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to dismiss alert.');
      }
    } catch (error) {
      logger.error('Failed to dismiss alert on server:', error);
    }
  }, [dismissAction, id, onDismiss]);

  // Early return if no dismissal capability needed
  if (!id || !dismissAction) {
    return children({ onClose: undefined });
  }

  return children({ onClose: handleClose });
};
